import React from 'react';

// Hero ends at x=58% (bottom).
// About reverses direction (triangle edge) — goes from 58% (top) to 70% (bottom).
// That's a +12% slope over 60vh (dramatic reverse), and the blue panel is wide
// throughout (42% wide at top, 30% wide at bottom) so text breathes.

const About = () => {
  return (
    // Mobile uses a slightly darker midnight (#14172A) so the About
    // section reads as a distinct surface from the Hero, which also
    // sits on bg-midnight-blue. Desktop keeps the original midnight
    // via the md: variant.
    <section
      id="about"
      className="relative lg:h-[540px] overflow-hidden bg-[#14172A] md:bg-midnight-blue"
      data-testid="about-section"
    >
      {/* === LEFT panel : steel-blue (bio side now) === */}
      <div
        className="absolute inset-0 hidden lg:block bg-terracotta"
        style={{
          clipPath: 'polygon(0 0, 68% 0, 68% 100%, 0 100%)',
          WebkitClipPath: 'polygon(0 0, 68% 0, 68% 100%, 0 100%)',
        }}
        data-testid="about-left-panel"
      />

      {/* === RIGHT side : plain midnight-blue (pattern removed per request) === */}

      {/* === Continuous vertical divider === */}
      <svg
        aria-hidden="true"
        className="hidden lg:block absolute inset-0 w-full h-full z-20 pointer-events-none"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
        data-testid="about-divider"
      >
        <line
          x1="68"
          y1="0"
          x2="68"
          y2="100"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="0.15"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* === CONTENT === */}
      <div className="relative z-10 h-full py-12 lg:py-0">
        <div className="container mx-auto px-8 md:px-16 h-full flex items-center">
          {/* LEFT : bio with text on left, portrait on right (desktop) */}
          <div className="w-full lg:max-w-[63%] lg:pl-[1cm] text-left flex flex-col lg:flex-row-reverse gap-6 lg:gap-7 items-center lg:items-start" data-testid="about-left-content">
            <div className="flex-shrink-0 w-40 h-52 sm:w-48 sm:h-60 lg:w-52 lg:h-64 xl:w-56 xl:h-[18rem] rounded-2xl overflow-hidden shadow-xl border-4 border-white/30 mt-2">
              <img
                src="/assets/portfolio/carol-mezher-profile-home.webp"
                alt="Carol Mezher — Writer, Producer and Creative Consultant, Paris"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* lg:pr-[1cm] shrinks the text column from the right on
                desktop so the paragraphs no longer butt up against the
                portrait that flex-row-reverse positions on the right. */}
            <div className="flex-1 w-full lg:pr-[1cm]">
              <h2 className="font-playfair font-bold text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-white lg:text-midnight-blue mb-5 lg:mb-7 text-center lg:text-left">
                Writer, Producer &amp; Creative Consultant
              </h2>

              <div className="space-y-5 text-base md:text-[1.05rem] text-white/85 leading-[1.8]" style={{ textAlign: 'justify', hyphens: 'auto', WebkitHyphens: 'auto' }}>
                <p style={{ textAlign: 'justify' }}>
                  With over 15 years of international experience across film, media, large-scale events, entertainment, and cultural industries, I develop stories and projects from concept to audience.
                </p>
                <p style={{ textAlign: 'justify' }}>
                  My work bridges creative development, film production, content curation, and live experiences, combining storytelling, project management, and industry strategy to bring local narratives to global audiences.
                </p>
                <p style={{ textAlign: 'left' }}>
                  Working at the intersection of Arab creative industries and international collaboration, I advise on cultural programming and cross-border partnerships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT : Core Expertise — absolutely positioned inside the blue panel */}
      <div
        className="hidden lg:flex absolute top-[97px] right-[3.5%] w-[26%] flex-col z-10"
        data-testid="about-right-content"
      >
        <h3 className="text-[20px] tracking-[0.2em] uppercase text-white/90 mb-7 font-montserrat font-bold">
          Core Expertise
        </h3>

        <div className="flex flex-col gap-7">
          {[
            {
              title: 'Story & Content Development',
              accent: 'From pitch to script',
              body: 'Film & TV development, script consulting, brand storytelling',
            },
            {
              title: 'Creative Direction & Production',
              accent: 'From script to screen',
              body: 'Film production, immersive narrative experiences, live shows',
            },
            {
              title: 'Strategy & Cultural Programming',
              accent: 'From story to audience',
              body: 'Festival curation, editorial content, cultural event management',
            },
          ].map((item) => (
            <div key={item.title} className="flex flex-col">
              <h4 className="text-lg xl:text-xl text-terracotta font-montserrat font-bold leading-tight mb-2">
                {item.title}
              </h4>
              <p className="text-sm xl:text-base text-white/95 font-montserrat leading-relaxed">
                <span className="text-white font-bold">{item.accent}</span> — {item.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Core Expertise — midnight-blue with hero-pattern backdrop
          (matches the Hero / About-page ribbons treatment). Mobile only;
          the desktop block above is untouched. */}
      <div className="lg:hidden relative overflow-hidden bg-midnight-blue py-12 px-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'url(/hero-pattern.png)',
            backgroundRepeat: 'repeat',
            backgroundSize: '760px auto',
            backgroundPosition: 'top left',
          }}
        />
        <div className="relative z-10">
          {/* Title styled to mirror the "Featured Work" heading: same font,
              size, tracking and centering. text-white kept for legibility on
              the midnight-blue backdrop. */}
          <div className="text-center mb-8">
            <h3 className="text-[20px] tracking-[0.2em] uppercase text-white font-montserrat font-bold">
              Core Expertise
            </h3>
          </div>
          <div className="space-y-5">
            <div>
              <h4 className="text-[22px] text-whale-blue font-montserrat font-bold mb-1.5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>Story &amp; Content Development</h4>
              <p className="text-base text-white/90"><span className="text-white font-bold">From pitch to script</span> — Film &amp; TV development, script consulting, brand storytelling</p>
            </div>
            <div>
              <h4 className="text-[22px] text-whale-blue font-montserrat font-bold mb-1.5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>Creative Direction &amp; Production</h4>
              <p className="text-base text-white/90"><span className="text-white font-bold">From script to screen</span> — Film production, immersive narrative experiences, live shows</p>
            </div>
            <div>
              <h4 className="text-[22px] text-whale-blue font-montserrat font-bold mb-1.5" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>Strategy &amp; Cultural Programming</h4>
              <p className="text-base text-white/90"><span className="text-white font-bold">From story to audience</span> — Festival curation, editorial content, cultural event management</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
